import { useEffect, useState } from "react";
import {
  EndpointsEnum,
  NameIdType,
} from "../../assets/utils/enums/project";
import { currencies } from "../../assets/utils/data/items";
import { useNavigate } from "react-router-dom";
import { useFormValidation } from "../../assets/utils/hooks/useFormsValidation";
import getData from "../../api/project-request";
import {
  API_URL_COMPANY,
  API_URL_CONTACT,
  API_URL_PROJECT,
  API_URL_SELLING,
} from "../../constants";
import { authFetch } from "../../api/authFetch";
import { formatIOS } from "../../assets/utils/date-formatter";
import toast from "react-hot-toast";
import CustomToast from "../../components/ui/custom-toast";
import { ToastTypeEnum } from "../../assets/utils/enums/toast";
import AppLayout, { MainBody } from "../../components/layout/app/layout";
import Headline from "../../components/ui/headline";
import { HeadlineTypeEnum } from "../../assets/utils/enums/headline";
import { ThemeTypeEnum } from "../../assets/utils/validation/types";
import { BodyDiv } from "../../components/components-style";
import {
  CaptionText,
  LittleSubHeadline,
} from "../../components/typography";
import Input from "../../components/ui/inputs";
import DeadlinePicker from "../../components/ui/deadline-picker";
import ErrorBlock from "../../components/ui/error-text";
import { Button } from "../../components/ui/button";
import { COLORS } from "../../assets/utils/enums/colors";
import Dropdown from "../../components/ui/dropdown";
import { DropdownTypeEnum } from "../../assets/utils/enums/table";
import { validateSelling } from "../../assets/utils/validation/form/validator-selling";
import { ValidationFormsModeEnum } from "../../assets/utils/validation/form/types";
import { SellingStage } from "../../assets/utils/enums/selling";

export default function AddSellingPage() {
  const [name, setName] = useState<string>("");
  const [client, setClient] = useState<string>("");
  const [project, setProject] = useState<NameIdType>({ id: "", name: "" });

  const [responsibleContact, setResponsibleContact] = useState<string>("");
  const [budget, setBudget] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [stage, setStage] = useState<string>(SellingStage.FIRST_CONTRACT);
  const [deadline, setDeadline] = useState<string>("");
  const [currency, setCurrency] = useState<string>(currencies[1].symbol);

  const [filterProjects, setFilterProjects] = useState<NameIdType[]>([]);
  const [projectQuery, setProjectQuery] = useState<string>("");

  const [filterClients, setFilterClients] = useState<
    { id: number; fullName: string; email: string }[]
  >([]);
  const [filterResponsible, setFilterResponsible] = useState<
    { fullName: string; email: string }[]
  >([]);
  const [filterCompanies, setFilterCompanies] = useState<
    { id: number; name: string; responsibleEmail: string }[]
  >([]);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { errors, validate } = useFormValidation(validateSelling);
  useEffect(() => {
    const getClients = async () => {
      const data = await getData(`${API_URL_CONTACT}/search?query=${client}`);
      setFilterClients(data.slice(0, 3));
    };
    const getCompanies = async () => {
      const data = await getData(`${API_URL_COMPANY}/search?query=${company}`);
      setFilterCompanies(data.slice(0, 3));
    };
    const getResponsibleUsers = async () => {
      const data = await getData(
        ` ${API_URL_PROJECT}/${EndpointsEnum.RESPONSIBLE_USER}?query=${responsibleContact}
     `
      );
      setFilterResponsible(data.slice(0, 4));
    };
    
    getClients();
    getCompanies();
    getResponsibleUsers();

  }, [client, company, responsibleContact]);

  useEffect(() => {
    // автозаповнення компанії, якщо компанія введена повністю

    const getCompanyByClient = async (id: number) => {
      const data = await getData(`${API_URL_CONTACT}/${id}/company`);

      if (data) {
        setCompany(data.company?.name ?? "");
      } else {
        setClient("");
      }
    };
    const exactClient = filterClients.find((x) => x.email === client);
    if (exactClient) {
      getCompanyByClient(exactClient.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    // автозаповнення клієнта, якщо компанія введена повністю
    const getClientByCompany = async (id: number) => {
      const data = await getData(`${API_URL_COMPANY}/${id}/main-contact`);
      if (data) {
        setClient(data.contact.email);
      } else {
        setCompany("");
      }
    };

    const exactCompany = filterCompanies.find((x) => x.name === company);
    if (exactCompany) {
      getClientByCompany(exactCompany.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (projectQuery !== project.name) {
      setProject({ id: "", name: "" });
    }

    const getProjects = async () => {
      try {
        const data = await getData(
          `${API_URL_PROJECT}/get-projects?query=${projectQuery}&sellingId=`
        );
        setFilterProjects(Array.isArray(data) ? data.slice(0, 3) : []);

      } catch {
        setFilterProjects([]);
      }
    };

    getProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQuery]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const { hasErrors } = validate(
        {
          name,
          client,
          company,
          description,
          deadline,
          budget,
          responsibleContact,
          stage,
        },
        ValidationFormsModeEnum.ADD
      );

      if (hasErrors) return;

      const response = await authFetch(`${API_URL_SELLING}/create-selling`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name,
          description: description,
          valutaCode:
            currencies.find((x) => x.symbol === currency)?.code || "UAH",
          amount: Number.parseInt(budget),
          deadline: formatIOS(deadline),
          stageName: stage,
          responsibleUserEmail: responsibleContact,
          contactEmail: client,
          companyId:
            filterCompanies.find((x) => x.name === company)?.id || null,
          projectId: project.name === "" || !project?.id ? null : project.id,
        }),
        credentials: "include",
      });
      if (!response.ok) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.WARNING}
            text={"Перевірте коректність введення даних!"}
          />
        ));
        return;
      }
      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.SUCCESS}
          text={"Дані введені коректно, продаж доданий!"}
        />
      ));
      setTimeout(() => {
        navigate("/selling/main");
      }, 300);
    } catch (error) {
      console.error("Fetch error:", error);
      toast((t) => (
        <CustomToast
          t={t}
          text={"Упс, щось пішло не так\nПроблема з сервером."}
          type={ToastTypeEnum.ERROR}
        />
      ));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 150);
    }
  };

  return (
    <AppLayout
      className="d-flex flex-column align-items-center"
      blockType={"item-form"}
      loaderState={isLoading}
    >
      <Headline
        headline={HeadlineTypeEnum.SELLING}
        theme={ThemeTypeEnum.LIGHT}
        btn_text="Повернутися до Продажів"
        btn_link="/selling/main"
      />
      <MainBody className="align-items-stretch">
        <BodyDiv
          data-class="add-block"
          className="d-flex flex-column align-items-center"
        >
          <CaptionText>Основні дані про продаж</CaptionText>
          <div className="w-100">
            <Input
              attribute="nameOfSelling"
              value={name}
              setValue={setName}
              errorText={errors.name}
              label="Назва *"
              placeholder="Введіть назву проєкту"
            />
            <span className="w-100 d-flex gap-sm-3 flex-sm-row flex-column">

              <Input
                attribute="client"
                value={client}
                setValue={setClient}
                errorText={errors.client}
                label="Клієнт"
                contactsArr={filterClients}
                placeholder="Введіть пошту / ім’я"
              />
              <Input
                attribute="company"
                value={company}
                setValue={setCompany}
                errorText={errors.company}
                companyArr={filterCompanies}
                label="Компанія"
                placeholder="Введіть назву компанії"
              />
            </span>
            <Input
              attribute={DropdownTypeEnum.PROJECTS}
              item={project}
              setItem={setProject}
              value={projectQuery}
              setValue={setProjectQuery}
              errorText={errors.project}
              itemsNameIdArr={filterProjects}
              label="Проєкт"
              placeholder="Введіть назву"
            />
            <DeadlinePicker value={deadline} setValue={setDeadline} />
            {errors.deadline && <ErrorBlock errorText={errors.deadline} />}
          </div>
        </BodyDiv>
        <BodyDiv data-class="add-block-other" className="">
          <span className="w-100 d-flex flex-column">
            <CaptionText className="pb-2 text-center">Етап</CaptionText>
            <Dropdown
              checkedValue={stage}
              setChecked={setStage}
              type={DropdownTypeEnum.SELLING_STAGE}
            />
            {errors.stage && <ErrorBlock errorText={errors.stage} />}
          </span>

          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Відповідальний контакт</CaptionText>
            <Input
              attribute="contact"
              value={responsibleContact}
              setValue={setResponsibleContact}
              errorText={errors.responsibleContact}
              label=""
              contactsArr={filterResponsible}
              placeholder="Введіть пошту / ім’я"
            />
          </span>
          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Бюджет</CaptionText>
            <Input
              attribute="budget"
              value={budget}
              setValue={setBudget}
              errorText={errors.budget}
              currency={currency}
              setCurrency={setCurrency}
              label=""
              placeholder="Введіть значення"
            />
          </span>

          <span className="w-100 d-flex flex-column align-items-center  ">
            <CaptionText className="text-center">Опис</CaptionText>
            <Input
              attribute="description"
              value={description}
              setValue={setDescription}
              errorText={""}
              label=""
              placeholder="Введіть опис продажі"
            />
          </span>
        </BodyDiv>
      </MainBody>

      <Button
        bgcolor={COLORS.secondary1}
        txtcolor={COLORS.white}
        brcolor={"transparent"}
        width="30%"
        className="center-btn"
        brradius="24px"
        type="large"
        onClick={handleSave}
      >
        <LittleSubHeadline>Зберегти зміни</LittleSubHeadline>
      </Button>
    </AppLayout>
  );
}
